import { hoursToMilliseconds, millisecondsToHours, millisecondsToMinutes } from '@gonfalon/datetime/dateFns';
import { daysToMilliseconds } from '@gonfalon/datetime/daysToMilliseconds';
import { millisecondsToDays } from '@gonfalon/datetime/millisecondsToDays';
import { msg } from '@gonfalon/messages/msg';
import { convertFilters } from '@gonfalon/metrics/convertFilters';
import { fromMetricEventUrl } from '@gonfalon/metrics/fromMetricEventUrl';
import { isImplicitWHNEventKey } from '@gonfalon/metrics/isImplicitWHNEventKey';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import { isMetricEventUrl } from '@gonfalon/metrics/isMetricEventUrl';
import type {
  ContextAttributeFilter,
  EventPropertyFilter,
  MeasureType,
  Metric,
  MetricDataSource,
  MetricEventUrl,
  MetricEventUrlKind,
  MetricFilter,
} from '@gonfalon/metrics/types';
import type { ResourceActions } from '@gonfalon/permissions/actions';

export type AggregationOptionKey = 'count' | 'count-distinct-units' | 'sum' | 'average' | 'count_distinct';

export type AggregationOption = {
  key: AggregationOptionKey;
  name: string;
  description: string;
  sentencePrefix: string;
};

// A function so the copy resolves at render, after the messages provider mounts.
export const getAggregationOptions = (): AggregationOption[] => [
  {
    key: 'count',
    name: msg('metrics.metricDefinition.aggregationCount'),
    description: msg('metrics.metricDefinition.countDescription'),
    sentencePrefix: msg('metrics.metricDefinition.aggregationCount'),
  },
  {
    key: 'count_distinct',
    name: msg('metrics.metricDefinition.aggregationCountDistinct'),
    description: msg('metrics.metricDefinition.countDistinctDescription'),
    sentencePrefix: msg('metrics.metricDefinition.aggregationCountDistinct'),
  },
  {
    key: 'count-distinct-units',
    name: msg('metrics.metricDefinition.aggregationOccurrence'),
    description: msg('metrics.metricDefinition.occurrenceDescription'),
    sentencePrefix: msg('metrics.metricDefinition.prefixPercent'),
  },
  {
    key: 'sum',
    name: msg('metrics.metricDefinition.aggregationSum'),
    description: msg('metrics.metricDefinition.sumDescription'),
    sentencePrefix: msg('metrics.metricDefinition.aggregationSum'),
  },
  {
    key: 'average',
    name: msg('metrics.metricDefinition.aggregationAverage'),
    description: msg('metrics.metricDefinition.averageDescription'),
    sentencePrefix: msg('metrics.metricDefinition.aggregationAverage'),
  },
];

export type AnalysisUnitOption = {
  label: string;
  value: string;
  default?: boolean;
};

type EventKeySource = { eventKey?: string; dataSourceKey?: MetricDataSource['key'] };

/** An event key is only required when the metric reports events to LaunchDarkly. */
export const isEventKeyRequired = (dataSourceKey?: MetricDataSource['key']) =>
  !dataSourceKey || isLDHosted(dataSourceKey);

/** The `eventKey` to send to the API. WHN metrics fall back to their data source key. */
export const toSubmittedEventKey = ({ eventKey, dataSourceKey }: EventKeySource) =>
  eventKey || (isEventKeyRequired(dataSourceKey) ? eventKey : dataSourceKey);

/** The `eventKey` to seed into form state — never surfaces an implicit WHN key in the input. */
export const toEventKeyFormValue = ({ eventKey, dataSourceKey }: EventKeySource) =>
  isImplicitWHNEventKey({ eventKey, dataSourceKey }) ? '' : (eventKey ?? '');

export function fromMetricToEditForm(metric: Metric): MetricForm {
  const { urls, analysisUnits, randomizationUnits, tags, filters, ...rest } = metric;
  let measureType: MeasureType = 'value';
  if (!metric.isNumeric) {
    if (metric.unitAggregationType === 'count_distinct') {
      measureType = 'value';
    } else if (metric.unitAggregationType === 'average') {
      measureType = 'occurrence';
    } else {
      measureType = 'count';
    }
  }

  let typecastedFilters: MetricFilter | undefined;
  if (filters) {
    typecastedFilters = convertFilters(filters);
  }

  // Detect unit from the end offset when start is 0, since 0 is divisible by every unit.
  const windowUnitSource = metric.windowStartOffset || metric.windowEndOffset;
  const windowUnit = windowUnitSource !== undefined ? detectWindowUnit(windowUnitSource) : 'days';
  const toUnit =
    windowUnit === 'days' ? millisecondsToDays : windowUnit === 'hours' ? millisecondsToHours : millisecondsToMinutes;

  // Map API winsorization fields to form structure. Default missing bounds to
  // the no-op values the create flow uses so partial winsorization can be edited.
  const { winsorLowerPercentile, winsorUpperPercentile } = metric;
  const hasWinsorization = winsorLowerPercentile !== undefined || winsorUpperPercentile !== undefined;
  const winsorization:
    | {
        lowerPercentile?: number;
        upperPercentile?: number;
      }
    | undefined = hasWinsorization
    ? {
        lowerPercentile: winsorLowerPercentile ?? 0,
        upperPercentile: winsorUpperPercentile ?? 100,
      }
    : undefined;

  const apiDenominator = metric.denominator;
  let denominatorForm: DenominatorForm | undefined;
  if (apiDenominator) {
    const denomWindowUnitSource = apiDenominator.windowStartOffset || apiDenominator.windowEndOffset;
    const denomWindowUnit = denomWindowUnitSource !== undefined ? detectWindowUnit(denomWindowUnitSource) : 'days';
    const toDenomUnit =
      denomWindowUnit === 'days'
        ? millisecondsToDays
        : denomWindowUnit === 'hours'
          ? millisecondsToHours
          : millisecondsToMinutes;

    const denom = apiDenominator;
    const denomHasWinsorization =
      apiDenominator.winsorLowerPercentile !== undefined || apiDenominator.winsorUpperPercentile !== undefined;
    function getDenomAggregationType(): RatioTermAggregation {
      const uatStr = denom.unitAggregationType;
      if (uatStr === 'count_distinct') {
        return 'count_distinct';
      }
      if (denom.isNumeric && uatStr === 'sum') {
        return 'sum';
      }
      if (denom.isNumeric && uatStr === 'average') {
        return 'average';
      }
      return 'count';
    }

    denominatorForm = {
      eventKey: toEventKeyFormValue({
        eventKey: apiDenominator.eventName,
        dataSourceKey: apiDenominator.dataSource?.key,
      }),
      dataSourceKey: apiDenominator.dataSource?.key,
      aggregationType: getDenomAggregationType(),
      unitAggregationField: apiDenominator.unitAggregationField,
      valueColumn: apiDenominator.valueColumn,
      filters: apiDenominator.filters ? convertFilters(apiDenominator.filters) : undefined,
      windowMode:
        apiDenominator.windowStartOffset !== undefined || apiDenominator.windowEndOffset !== undefined
          ? 'custom'
          : 'none',
      windowUnit: denomWindowUnit,
      windowStart:
        apiDenominator.windowStartOffset !== undefined ? toDenomUnit(apiDenominator.windowStartOffset) : undefined,
      windowEnd: apiDenominator.windowEndOffset !== undefined ? toDenomUnit(apiDenominator.windowEndOffset) : undefined,
      winsorLowerEnabled: denomHasWinsorization,
      winsorLowerPercentile: denomHasWinsorization ? (apiDenominator.winsorLowerPercentile ?? 0) : undefined,
      winsorUpperEnabled: denomHasWinsorization,
      winsorUpperPercentile: denomHasWinsorization ? (apiDenominator.winsorUpperPercentile ?? 100) : undefined,
    };
  }

  return {
    ...rest,
    filters: typecastedFilters,
    analysisUnits: (analysisUnits ?? []).map((value) => ({ label: value, value })),
    tags: tags?.map((value) => ({ label: value, value })),
    urls: urls?.filter(isMetricEventUrl).map(fromMetricEventUrl),
    unitAggregationType: rest?.unitAggregationType ?? 'average',
    analysisType: rest?.analysisType ?? 'mean',
    //TODO: what should happen if an invalid maintainer id is loaded?
    maintainerId: metric.maintainerId ?? '',
    eventDefault: {
      disabled: rest.eventDefault?.disabled ?? false,
      ...(rest.eventDefault?.value !== undefined && { value: rest.eventDefault.value }),
    },
    measureType,
    ratioOrSimple: apiDenominator ? 'ratio' : 'simple',
    dataSourceKey: metric.dataSource?.key,
    eventKey: toEventKeyFormValue({ eventKey: metric.eventKey, dataSourceKey: metric.dataSource?.key }),
    windowMode: metric.windowStartOffset !== undefined || metric.windowEndOffset !== undefined ? 'custom' : 'none',
    windowUnit,
    windowStart: metric.windowStartOffset !== undefined ? toUnit(metric.windowStartOffset) : undefined,
    windowEnd: metric.windowEndOffset !== undefined ? toUnit(metric.windowEndOffset) : undefined,
    traceQuery: metric.traceQuery,
    winsorization,
    denominator: denominatorForm,
  };
}

// fields that are included in the create metric modal, deprecate after new create modal launch
export type NewMetricForm = EditMetricForm & {
  key: string;
  kind: 'click' | 'pageview' | 'custom';
  isNumeric?: boolean;
  maintainerId: string;
};

export type WindowUnit = 'days' | 'hours' | 'minutes';

/** Aggregation type for a ratio metric term (numerator or denominator). */
export type RatioTermAggregation = 'count' | 'sum' | 'average' | 'count_distinct';

/** Form state for the denominator term of a ratio metric. */
export type DenominatorForm = {
  eventKey: string;
  dataSourceKey?: string;
  aggregationType: RatioTermAggregation;
  unitAggregationField?: string;
  valueColumn?: string;
  filters?: MetricFilter;

  windowMode: 'none' | 'custom';
  windowUnit: WindowUnit;
  windowStart?: number;
  windowEnd?: number;
  winsorLowerEnabled?: boolean;
  winsorLowerPercentile?: number;
  winsorUpperEnabled?: boolean;
  winsorUpperPercentile?: number;
};

export const defaultDenominatorForm: DenominatorForm = {
  eventKey: '',
  aggregationType: 'count',
  windowMode: 'none',
  windowUnit: 'days',
  winsorLowerEnabled: false,
  winsorUpperEnabled: false,
};

function detectWindowUnit(ms: number): WindowUnit {
  if (ms % daysToMilliseconds(1) === 0) {
    return 'days';
  }
  if (ms % hoursToMilliseconds(1) === 0) {
    return 'hours';
  }
  return 'minutes';
}

export type MetricForm = {
  key: string;
  kind: Metric['kind'];
  isNumeric?: boolean;
  maintainerId: string;
  name: string;
  description?: string;
  eventKey?: string;
  unit?: string;
  filters?: MetricFilter;
  successCriteria?: Metric['successCriteria'];
  urls?: Array<{ kind: MetricEventUrlKind; value: string }>;
  selector?: string;
  tags?: Array<{ label: string; value: string }> | null;
  analysisUnits: AnalysisUnitOption[];
  unitAggregationType?: Metric['unitAggregationType'];
  analysisType?: Metric['analysisType'];
  percentileValue?: number;
  eventDefault: { disabled: boolean; value?: number };
  measureType?: MeasureType;
  dataSourceKey?: MetricDataSource['key'];
  /** Controls whether the measurement window is active. */
  windowMode: 'none' | 'custom';
  /** Unit used for displaying/entering window offsets. */
  windowUnit: WindowUnit;
  /** Window start offset in the selected unit (converted to ms when sent to the API). */
  windowStart?: number;
  /** Window end offset in the selected unit (converted to ms when sent to the API). */
  windowEnd?: number;
  /** ANTLR query string for trace metrics, derived from the QueryBuilder. */
  traceQuery?: string;
  /** For numeric trace metrics, the span attribute key whose value is measured. */
  traceValueLocation?: string;
  /** Whether this is a simple or ratio metric. Only applies to custom metrics. */
  ratioOrSimple?: 'simple' | 'ratio';
  /** Warehouse column to count distinct values of. Required when unitAggregationType is count_distinct. */
  unitAggregationField?: string;
  valueColumn?: string;
  /** Denominator configuration for ratio metrics. */
  denominator?: DenominatorForm;
  /** Winsorization configuration for handling outliers. */
  winsorization?: {
    lowerPercentile?: number;
    upperPercentile?: number;
    /** Hidden validation field to ensure at least one bound is configured */
    _validation?: unknown;
  };
};

// fields that are editable in the metric details form, deprecate after new edit modal launch
export type EditMetricForm = {
  name: string;
  description?: string;
  eventKey?: string;
  unit?: string;
  successCriteria?: Metric['successCriteria'];
  urls?: Array<{ kind: MetricEventUrlKind; value: string }>;
  selector?: string;
  tags?: Array<{ label: string; value: string }> | null;
  analysisUnits: Array<{ label: string; value: string }>;
  unitAggregationType: 'average' | 'sum' | 'count_distinct';
  analysisType: 'mean' | 'percentile';
  percentileValue?: number;
  eventDefault: { disabled: boolean; value?: number };
  maintainerId?: string;
};

export const FIELD_NAME_TO_ACTION = new Map<string, ResourceActions<'metric'>>([
  ['name', 'updateName'],
  ['description', 'updateDescription'],
  ['urls', 'updateUrls'],
  ['selector', 'updateSelector'],
  ['tags', 'updateTags'],
  ['isNumeric', 'updateNumeric'],
  ['unit', 'updateNumericUnit'],
  ['successCriteria', 'updateNumericSuccess'],
  ['maintainerId', 'updateMaintainer'],
  ['eventKey', 'updateEventKey'],
  ['analysisUnits', 'updateRandomizationUnits'],
  ['unitAggregationType', 'updateUnitAggregationType'],
  ['analysisType', 'updateAnalysisType'],
  ['eventDefault', 'updateEventDefault'],
]);

export type DenominatorInput = {
  eventName: string;
  isNumeric: boolean;
  unitAggregationType?: string;
  unitAggregationField?: string;
  valueColumn?: string;
  filters?: MetricFilter;
  dataSource?: { key: string };
  windowStartOffset?: number;
  windowEndOffset?: number;
  winsorLowerPercentile?: number;
  winsorUpperPercentile?: number;
};

export const defaultContextAttributeFilter: ContextAttributeFilter = {
  type: 'contextAttribute',
  op: 'in',
  contextKind: 'user',
  values: [],
  negate: false,
};

export const defaultEventPropertyFilter: EventPropertyFilter = {
  type: 'eventProperty',
  op: 'in',
  values: [],
  negate: false,
};

export type MetricPost = Omit<
  MetricForm,
  | 'tags'
  | 'analysisUnits'
  | 'urls'
  | 'kind'
  | 'windowMode'
  | 'windowUnit'
  | 'windowStart'
  | 'windowEnd'
  | 'ratioOrSimple'
  | 'denominator'
  | 'dataSourceKey'
  | 'winsorization'
> & {
  kind: 'click' | 'pageview' | 'custom' | 'trace';
  tags?: string[];
  analysisUnits?: string[];
  urls?: MetricEventUrl[];
  windowStartOffset?: number;
  windowEndOffset?: number;
  denominator?: DenominatorInput;
  dataSource?: { key: string };
  isActive: boolean;
  winsorLowerPercentile?: number;
  winsorUpperPercentile?: number;
};
